// Imports
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { t } from "i18next";
import { CONST } from "models/constants";
import { shallowEqual, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { formatDate } from "util/datelib";
import MoreAction, { DropDownItem } from "widgets/controls/moreAction";

// Define the structure of the JdTableModel
export class ReticulationPageTableModel {
  _id: string;
  name: string;
  reticulationType: string;
  sites: string | string[];
  created: string;
  actions: string;
}
const SiteCell: React.FC<{ siteIds: string | string[] }> = ({ siteIds }) => {
  const allSites = useSelector(accountSiteList, shallowEqual);
  const ids = Array.isArray(siteIds) ? siteIds : [siteIds];
  const displayNames = ids.map(
    (id) => allSites.find((s) => s.id === id)?.name ?? id
  );
  return (
    <span className="flex flex-grow flex-row gap-1 truncate">
      {displayNames.join(", ")}
    </span>
  );
};

const defineReticulationTablecolumns = (
  onViewClick?: (info: CellContext<ReticulationPageTableModel, any>) => void,
  onEditClick?: (info: CellContext<ReticulationPageTableModel, any>) => void,
  onDesignClick?: (info: CellContext<ReticulationPageTableModel, any>) => void,
  onRemoveClick?: (info: CellContext<ReticulationPageTableModel, any>) => void
) => {
  // Initialize the translation hook and
  const moreMenuItems: DropDownItem[] = [
    {
      label: t("button.action.view"),
      active: true,
      onSelected: onViewClick,
      permissionRole: CONST.ROLE_MANAGER,
    },
    {
      label: t("button.action.rename"),
      active: true,
      onSelected: onEditClick,
      permissionRole: CONST.ROLE_MANAGER,
    },
    {
      label: t("button.action.design"),
      active: true,
      onSelected: onDesignClick,
      permissionRole: CONST.ROLE_MANAGER,
    },
    {
      label: t("button.action.remove"),
      active: true,
      onSelected: onRemoveClick,
      permissionRole: CONST.ROLE_OWNER,
    },
  ];
  // create a column helper for the JdTableModel
  const columnHelper = createColumnHelper<ReticulationPageTableModel>();

  // Define the columns for the JdTable
  const reticulationTablecolumns = [
    columnHelper.accessor("name", {
      enableSorting: false,
      header: () => <span>{t("table.header.name")}</span>,
      cell: (info) => <span>{info.getValue()}</span>,
      meta: { rowClick: true },
    }),
    columnHelper.accessor("created", {
      enableSorting: true,
      header: () => <span>{t("table.header.created", "Created")}</span>,
      cell: (info) => (
        <span className="">{formatDate(info.getValue(), "lastcomms")}</span>
      ),
      meta: { rowClick: true },
    }),
    columnHelper.accessor("reticulationType", {
      enableSorting: false,
      header: () => <span>{t("table.header.type", "Type")}</span>,
      cell: (info) => <span>{info.getValue()}</span>,
      meta: { rowClick: true },
    }),
    columnHelper.accessor("sites", {
      enableSorting: false,
      meta: { rowClick: true },
      header: () => <span className="">{t("table.header.sites")}</span>,
      cell: (info): JSX.Element => {
        const sites = info.row.original.sites;
        const siteIds: string | string[] = Array.isArray(sites)
          ? sites
          : typeof sites === "string"
          ? sites
          : sites != null
          ? [String(sites)]
          : [];
        return <SiteCell siteIds={siteIds} />;
      },
    }),
    columnHelper.accessor("actions", {
      enableSorting: false,
      meta: {
        rowClick: false,
        customClass: "justify-items-center",
        customHeadClass: "justify-items-center",
      },
      header: () => <span>{t("table.header.actions")}</span>,
      cell: (info) => (
        <div>
          <MoreAction
            items={moreMenuItems}
            info={info}
            title={t("button.action.options", "Options")}
            extra="w-full flex"
          />
        </div>
      ),
    }),
  ];

  return reticulationTablecolumns;
};

export default defineReticulationTablecolumns;
